import * as Yup from "yup";
import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { TriangleWarning } from "components/icons/TriangleWarning";
import { PriceInput } from "components/PriceInput/PriceInput";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useToast } from "hooks/useToast";
import { useAddBenefitEntryMutation } from "store/apis/estimatedTax";
import { ManualBenefitCategory } from "types/Models/estimatedTax";
import { BackendError } from "types/utils/error";
import { YEAR_OPTIONS } from "../constants";
import { MANUAL_BENEFIT_CATEGORY_COPY } from "./constants";

type Props = {
  open: boolean;
  onClose: () => void;
  entityId: string;
  category: ManualBenefitCategory;
};

type AddEntryForm = {
  tax_year: string;
  original_amount: string;
};

const INITIAL_VALUES: AddEntryForm = { tax_year: "", original_amount: "" };

const VALIDATION_SCHEMA = Yup.object({
  tax_year: Yup.string().required("Tax year is required"),
  original_amount: Yup.number()
    .typeError("Enter a valid amount")
    .positive("Amount must be greater than 0")
    .required("Amount is required"),
});

export const AddTaxBenefitEntryModal = ({
  open,
  onClose,
  entityId,
  category,
}: Props) => {
  const { alertToast, successToast } = useToast();
  const [addBenefitEntry, { isLoading }] = useAddBenefitEntryMutation();

  const copy = MANUAL_BENEFIT_CATEGORY_COPY[category];

  const handleSubmit = async (values: AddEntryForm) => {
    try {
      await addBenefitEntry({
        entityId,
        payload: {
          category,
          tax_year: Number(values.tax_year),
          original_amount: Number(values.original_amount),
        },
      }).unwrap();
      successToast({ message: copy.successMessage });
      onClose();
    } catch (e) {
      alertToast({
        message:
          (e as BackendError)?.data?.error?.message ?? "Something went wrong",
      });
    }
  };

  return (
    <Modal.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Modal.Content useCustomOverlay>
        <FormikForm<AddEntryForm>
          initialValues={INITIAL_VALUES}
          validationSchema={VALIDATION_SCHEMA}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue }) => (
            <>
              <Modal.Header>
                <Modal.Title className="t-text-[20px]">
                  {copy.modalTitle}
                </Modal.Title>
                <Modal.Close onClick={onClose} />
              </Modal.Header>
              <Modal.Body className="t-space-y-4">
                <Banner
                  type="warning"
                  icon={
                    <span className="t-flex t-shrink-0 t-self-start t-text-orange-80">
                      <TriangleWarning width="18" height="18" />
                    </span>
                  }
                  description="We normally import business losses from your previous tax filings. Only add one manually if it hasn't already been imported."
                />
                <div className="t-grid t-grid-cols-2 t-gap-2">
                  <Combobox
                    name="tax_year"
                    label={copy.yearLabel}
                    required
                    options={YEAR_OPTIONS}
                    placeholder="Select year"
                    menuPortalTarget={document.body}
                    value={
                      YEAR_OPTIONS.find((o) => o.value === values.tax_year) ??
                      null
                    }
                    onChange={(option) => {
                      const opt = option as OptionData | null;
                      setFieldValue("tax_year", opt?.value ?? "");
                    }}
                  />
                  <PriceInput
                    name="original_amount"
                    label={copy.amountLabel}
                    required
                    allowNegative={false}
                    placeholder="Enter amount"
                  />
                </div>
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button size="regular" disabled={isLoading}>
                    Cancel
                  </Button>
                </Modal.RawClose>
                <Button
                  type="submit"
                  customType="primary"
                  size="regular"
                  isLoading={isLoading}
                  disabled={isLoading}
                >
                  {copy.submitLabel}
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
